import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getPaymentByDokuId, getDokuStatus, formatIDR } from '../services/portalService';
import './portal.css';

const PaymentReturn = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const dokuOrderId = params.get('order');
  const [state, setState] = useState('checking'); // checking | success | pending | failed | unknown
  const [payment, setPayment] = useState(null);
  const timer = useRef(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!dokuOrderId) { setState('unknown'); return; }
    let cancelled = false;

    const poll = async () => {
      tries.current += 1;
      try {
        const pay = await getPaymentByDokuId(dokuOrderId);
        if (pay && !cancelled) {
          setPayment(pay);
          if (pay.status === 'success') { setState('success'); return; }
          if (['failed', 'expired'].includes(pay.status)) { setState('failed'); return; }
        }
        // Secondary check straight from Doku (in case the webhook is delayed).
        try {
          const s = await getDokuStatus(dokuOrderId);
          const txt = JSON.stringify(s?.data || {}).toLowerCase();
          if (/success|settle|paid|approve/.test(txt)) { if (!cancelled) setState('success'); return; }
        } catch (_) { /* server may be unreachable; rely on Supabase */ }
      } catch (_) { /* not signed in / RLS — keep trying via server */ }

      if (cancelled) return;
      if (tries.current >= 8) { setState((s) => (s === 'checking' ? 'pending' : s)); return; }
      timer.current = setTimeout(poll, 2500);
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer.current); };
  }, [dokuOrderId]);

  const orderUuid = payment?.order_id || payment?.orders?.id;
  const goDashboard = () => navigate(orderUuid ? `/dashboard?order=${orderUuid}` : '/dashboard');

  const VIEW = {
    checking: { emoji: '⏳', title: 'Confirming your payment…', sub: 'Hang tight, this only takes a moment.', spinner: true },
    success: { emoji: '✅', title: 'Payment successful!', sub: payment ? `We received ${formatIDR(payment.amount)}. A receipt is in your dashboard.` : 'Your payment was received.' },
    pending: { emoji: '🕒', title: 'Payment is processing', sub: 'We haven’t had final confirmation yet. It will update in your dashboard shortly.' },
    failed: { emoji: '⚠️', title: 'Payment not completed', sub: 'The payment was cancelled or failed. You can try again from your dashboard.' },
    unknown: { emoji: '🤔', title: 'No payment reference', sub: 'We couldn’t find a payment to confirm.' },
  }[state];

  return (
    <div className="portal" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="portal-card" style={{ maxWidth: 460, width: '100%', textAlign: 'center' }}>
        <div style={{ fontSize: 48 }}>{VIEW.emoji}</div>
        <h1 className="portal-title" style={{ fontSize: 28, marginTop: 10 }}>{VIEW.title}</h1>
        <p className="portal-sub" style={{ margin: '10px auto 24px' }}>{VIEW.sub}</p>
        {VIEW.spinner && <div className="portal-spinner" style={{ margin: '0 auto 24px' }} />}
        <button className="portal-btn portal-btn-primary portal-btn-block" onClick={goDashboard}>Go to my dashboard</button>
      </div>
    </div>
  );
};

export default PaymentReturn;
